import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { variantPrice } from "../lib/categories";

const CartContext = createContext(null);
const KEY = "va_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, color, size, qty = 1) => {
    if (product.status !== "active") {
      toast.error("This is a draft preview product and can't be purchased yet.");
      return false;
    }
    if (product.sold_out || (product.variants?.length && !product.variants.some((v) => v.color === color && v.size === size && v.is_available !== false))) {
      toast.error("That option is currently unavailable.");
      return false;
    }
    const key = `${product.slug}|${color}|${size}`;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + qty, 20) } : i));
      return [...prev, { key, slug: product.slug, name: product.name, price: variantPrice(product, color, size), image: product.images?.[0], color, size, qty }];
    });
    toast.success(`${product.name} added to cart`);
    setOpen(true);
    return true;
  };

  const updateQty = (key, qty) =>
    setItems((prev) => (qty < 1 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty: Math.min(qty, 20) } : i))));
  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const count = items.reduce((s, i) => s + i.qty, 0);
    return { items, add, updateQty, remove, clear, subtotal, count, open, setOpen };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
